import React, { useState } from 'react';
import { Button, Input, FormField } from '@neshane/blueberry';
import Modal from '../../../common/components/Modal/Modal';
import { getErrorMessage } from '../../../api/unwrapQuery';
import { getDastineRegistrationOptions, registerDastine } from '../api/securityApi';
import { useStepUp } from '../hooks/useStepUp';
import './DastineSetupWizard.css';

const DastineSetupWizard = ({ isOpen, onClose, onRegistered }) => {
    const [name, setName] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { runWithStepUp } = useStepUp();

    const handleRegister = async () => {
        setLoading(true);
        setError(null);
        try {
            const dastineAgent = window.Dastine || window.dastine;
            if (!dastineAgent) {
                throw new Error('no_extension');
            }

            await runWithStepUp('DASTINE_CHANGE', async (stepUpToken) => {
                const optionsRes = await getDastineRegistrationOptions(stepUpToken);
                const challenge = optionsRes?.challenge || optionsRes?.data?.challenge;
                if (!challenge) {
                    throw new Error('چالش دریافت نشد');
                }

                let signature;
                let certificate;

                if (window.dastine && typeof window.dastine.sign === 'function') {
                    try {
                        const result = await window.dastine.sign(challenge);
                        signature = result?.signature || result?.Signature || result?.data?.Result;
                        certificate = result?.certificate || result?.Certificate || result?.cert;
                        if (typeof result === 'string') signature = result;
                        if (result?.code === '18' || result?.Result === '18') {
                            throw new Error('user_cancelled');
                        }
                    } catch (err) {
                        if (err?.code === '18' || err?.Result === '18' || err?.message?.includes('لغو')) {
                            throw new Error('user_cancelled');
                        }
                        throw err;
                    }
                } else if (window.Dastine && typeof window.Dastine.Sign === 'function') {
                    const signResult = await new Promise((resolve, reject) => {
                        try {
                            window.Dastine.Sign(challenge, 'SHA256', (event) => {
                                const result = event?.data?.Result;
                                if (result === '18' || result === 18) {
                                    reject(new Error('user_cancelled'));
                                    return;
                                }
                                if (result && /^\d+$/.test(String(result)) && String(result) !== '0' && String(result) !== '00') {
                                    if (String(result) === '11' || String(result) === '27') {
                                        reject(new Error('invalid certificate'));
                                        return;
                                    }
                                    if (String(result) === '25') {
                                        reject(new Error('expired certificate'));
                                        return;
                                    }
                                    reject(new Error('خطا در امضای Dastine: ' + result));
                                    return;
                                }
                                resolve(result);
                            });
                        } catch (err) {
                            reject(err);
                        }
                    });
                    signature = signResult;

                    if (window.Dastine.GetSelectedCertificate) {
                        try {
                            const certResult = await new Promise((resolve, reject) => {
                                window.Dastine.GetSelectedCertificate((event) => {
                                    const res = event?.data?.Result;
                                    if (res && res !== '0' && !/^\d+$/.test(String(res))) {
                                        resolve(res);
                                    } else {
                                        reject(new Error('invalid certificate'));
                                    }
                                });
                            });
                            try {
                                const parsed = JSON.parse(certResult);
                                certificate = parsed?.certificate || parsed?.cert || certResult;
                            } catch {
                                certificate = certResult;
                            }
                        } catch (e) {
                            certificate = window.__dastineMockCertificate || null;
                        }
                    }
                } else {
                    throw new Error('no_extension');
                }

                if (!signature) {
                    throw new Error('امضا دریافت نشد');
                }
                if (!certificate) {
                    certificate = window.__dastineMockCertificate || null;
                    if (!certificate) {
                        throw new Error('invalid certificate');
                    }
                }

                if (!certificate.includes('BEGIN CERTIFICATE')) {
                    throw new Error('invalid certificate');
                }

                await registerDastine(
                    {
                        challenge,
                        signature,
                        certificate,
                        name: name.trim() || undefined,
                    },
                    stepUpToken
                );
            });

            if (onRegistered) await onRegistered();
            onClose();
        } catch (err) {
            const message = err?.message || '';
            const responseError = err?.response?.data?.error || err?.response?.data?.message || '';
            const fullMessage = responseError || message;
            if (message === 'no_extension' || fullMessage.includes('Dastine') && fullMessage.includes('یافت نشد') || message.includes('no_extension')) {
                setError('افزونه Dastine یافت نشد. لطفاً آن را نصب و فعال کنید.');
            } else if (message === 'user_cancelled' || err?.code === '18' || fullMessage.includes('لغو') || fullMessage.includes('cancelled') || fullMessage.includes('Cancel')) {
                setError('عملیات توسط کاربر لغو شد.');
            } else if (fullMessage.toLowerCase().includes('expired') || fullMessage.includes('منقضی')) {
                setError('گواهی منقضی شده است.');
            } else if (fullMessage.includes('invalid certificate') || fullMessage.includes('Invalid certificate') || fullMessage.includes('گواهی نامعتبر')) {
                setError('گواهی نامعتبر است.');
            } else {
                setError(getErrorMessage(err, 'خطا در ثبت Dastine') || 'خطا در ثبت Dastine');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="ثبت گواهی Dastine" size="medium">
            <div className="dastine-wizard">
                <p className="dastine-wizard__intro">
                    برای ثبت گواهی Dastine، ابتدا افزونه Dastine را نصب کنید، سپس گواهی خود را انتخاب و امضا کنید.
                </p>

                {error && <div className="dastine-wizard__error" role="alert">{error}</div>}

                <FormField label="نام گواهی (اختیاری)" htmlFor="dastine-name" infoTipContent="نام گواهی (اختیاری) - مثلاً: توکن اصلی">
                    <Input
                        id="dastine-name"
                        fit="parent"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="مثلاً: توکن اصلی"
                        maxLength={100}
                    />
                </FormField>

                <div className="dastine-wizard__actions">
                    <Button
                        color="primary"
                        fit="parent"
                        isDisabled={loading}
                        onClick={handleRegister}
                        text={loading ? 'در حال ثبت…' : 'ثبت Dastine'}
                    />
                    <Button variant="secondaryNeutral" fit="parent" onClick={onClose} text="انصراف" isDisabled={loading} />
                </div>

                <p className="dastine-wizard__hint">
                    کلید خصوصی هرگز ذخیره نمی‌شود. فقط اثر انگشت، کلید عمومی و اطلاعات گواهی ذخیره می‌شوند.
                </p>
            </div>
        </Modal>
    );
};

export default DastineSetupWizard;
